import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, Undo2, X } from "lucide-react";
import { Button } from "./ui/button";
import { autofillList, autofillConfirm, autofillUndo } from "../lib/api";
import { tr } from "../lib/i18n";

// Pops up shortly after an upload once the AI has filled things in elsewhere: one tap to keep, or undo.
export const AutofillReviewPrompt = ({ readOnly }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["autofill"], queryFn: autofillList, refetchInterval: 15_000, enabled: !readOnly });
  const [hidden, setHidden] = useState([]);
  const [busy, setBusy] = useState("");
  const pending = (data?.pending || []).filter((p) => !hidden.includes(p.id));
  const u = pending[0];
  if (readOnly || !u) return null;
  const act = async (kind) => {
    setBusy(kind);
    try {
      qc.setQueryData(["autofill"], kind === "undo" ? await autofillUndo(u.id) : await autofillConfirm(u.id));
      toast.success(tr(kind === "undo" ? "Undone" : "Kept"));
      qc.invalidateQueries({ queryKey: ["estate-plan"] });
    } catch (e) { toast.error(e?.response?.data?.detail || tr("Something went wrong")); } finally { setBusy(""); }
  };
  return (
    <div role="dialog" aria-live="polite" className="fixed z-50 bottom-24 right-4 left-4 sm:left-auto sm:w-[380px] rounded-2xl border border-primary/30 bg-card shadow-xl p-4 animate-in slide-in-from-bottom-4 fade-in duration-300" data-testid="autofill-review-prompt">
      <div className="flex items-start gap-3">
        <span className="h-8 w-8 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Sparkles className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{tr("Here's what we filled in")}</div>
          <div className="text-xs text-muted-foreground truncate" data-testid="autofill-review-doc">{tr("From")} “{u.doc_title}”</div>
        </div>
        <button type="button" onClick={() => setHidden((h) => [...h, u.id])} aria-label={tr("Later")} className="text-muted-foreground hover:text-foreground" data-testid="autofill-review-later"><X className="h-4 w-4" /></button>
      </div>
      <ul className="mt-3 space-y-1 text-sm" data-testid="autofill-review-changes">{u.changes.map((c, i) => <li key={i} className="flex gap-2"><Check className="h-3.5 w-3.5 mt-1 text-primary shrink-0" />{c.label}</li>)}</ul>
      <div className="mt-4 flex gap-2">
        <Button size="sm" className="flex-1 gap-1.5" disabled={!!busy} onClick={() => act("confirm")} data-testid="autofill-review-confirm">{busy === "confirm" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} {tr("Looks right")}</Button>
        <Button size="sm" variant="outline" className="flex-1 gap-1.5" disabled={!!busy} onClick={() => act("undo")} data-testid="autofill-review-undo">{busy === "undo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />} {tr("Undo")}</Button>
      </div>
      {pending.length > 1 && <p className="mt-2 text-[11px] text-muted-foreground text-center">+{pending.length - 1} {tr("more to review")}</p>}
    </div>
  );
};
